import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ShieldCheck, Building2, User, Mail, Hash, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris",
    }) + " (Paris)";
  } catch { return iso; }
}

export default function VerifyCertificate() {
  const { signatureId } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/certificates/verify/${encodeURIComponent(signatureId)}`)
      .then((res) => { if (!cancelled) setState({ loading: false, data: res.data }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err?.message || "Erreur réseau" }); });
    return () => { cancelled = true; };
  }, [signatureId]);

  const displayCode = signatureId
    ? [signatureId.slice(0, 5), signatureId.slice(5, 10), signatureId.slice(10, 15)].join(" · ").toUpperCase()
    : "";

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-lg bg-[#0066FF] flex items-center justify-center text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight text-slate-900">FIO<span className="text-[#0066FF]">TELECOM</span></span>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="verify-card">
          {state.loading && (
            <div className="p-10 text-center text-sm text-slate-500">Vérification en cours…</div>
          )}

          {!state.loading && state.error && (
            <div className="p-10 text-center">
              <XCircle className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-600">Impossible de vérifier cette signature ({state.error}).</p>
            </div>
          )}

          {!state.loading && state.data && state.data.valid && (
            <>
              <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-4 flex items-center gap-3" data-testid="verify-valid">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-heading font-semibold text-emerald-900">Signature authentique</div>
                  <p className="text-xs text-emerald-700 mt-0.5">Ce document a été validé électroniquement par la Direction des Ressources Humaines de FIO TELECOM.</p>
                </div>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <Row icon={Building2} label="Type de document" value={state.data.type} />
                <Row icon={User} label="Destinataire du document" value={state.data.user_name} />
                <Row icon={Mail} label="Email (masqué)" value={state.data.user_email_masked} />
                <Row icon={Building2} label="Émis pour" value={state.data.destinataire} />
                <Row icon={Calendar} label="Signé le" value={fmt(state.data.signed_at)} />
                <Row icon={User} label="Signataire" value={state.data.signed_by} />
                <Row icon={Hash} label="Code de vérification" value={displayCode} mono />
              </div>
            </>
          )}

          {!state.loading && state.data && !state.data.valid && (
            <>
              <div className="bg-rose-50 border-b border-rose-100 px-6 py-4 flex items-center gap-3" data-testid="verify-invalid">
                <XCircle className="h-6 w-6 text-rose-600 shrink-0" />
                <div>
                  <div className="font-heading font-semibold text-rose-900">Signature non valide</div>
                  <p className="text-xs text-rose-700 mt-0.5">{state.data.reason}</p>
                </div>
              </div>
              <div className="p-6 text-sm text-slate-600">
                Code contrôlé : <span className="font-mono font-semibold text-slate-900">{displayCode}</span>
              </div>
            </>
          )}
        </div>

        <p className="mt-4 text-xs text-slate-500 text-center">
          Cette page publique confirme l'existence et l'authenticité d'une attestation FIO TELECOM. Elle n'expose aucune information confidentielle.
        </p>
        <p className="mt-2 text-xs text-slate-400 text-center">
          <Link to="/" className="hover:text-slate-700 underline">← Retour au site</Link>
        </p>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className={`text-slate-900 mt-0.5 break-words ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
      </div>
    </div>
  );
}
